<template>
  <dp-seite titel="Rotationsmodelle" untertitel="Optionale Vorschläge – manuelle Zuordnungen bleiben möglich" :snack="snack">
    <template #aktionen>
      <v-btn small depressed @click="$router.push('/dienstplanung/zuordnung')"><v-icon small left>mdi-calendar-edit</v-icon>Musterzuordnung</v-btn>
    </template>

    <template v-if="dp.plan">
      <section class="dp-sec">
        <h2>Rotation festlegen <small>Eine Kolonne durchläuft die Wochen einer Phase eines vorhandenen Arbeitszeitrasters</small></h2>
        <v-alert v-if="!settings.can_edit" type="info" dense outlined>Sie haben nur Leserechte. Die Vorschau ist möglich, Vorschläge können nicht vorgemerkt werden.</v-alert>
        <div class="d-flex flex-wrap align-center" style="gap: 12px">
          <v-select v-model="crewId" :items="kolonnenItems" item-text="bezeichnung" item-value="id" outlined dense hide-details label="Kolonne"
                    no-data-text="Keine aktive Kolonne vorhanden." style="min-width: 240px; max-width: 320px"/>
          <v-autocomplete v-model="azra" :items="dp.basis.raster" item-text="name" item-value="bis_id" outlined dense hide-details label="Arbeitszeitraster"
                          no-data-text="Kein Arbeitszeitraster vorhanden." style="min-width: 280px; max-width: 400px"/>
          <dp-date-field v-model="anker" label="Anker-Montag" :allowed-dates="nurMontag" style="max-width: 190px"/>
        </div>
        <div class="mt-3"><zeitraum-wahl :value="dp.range" @input="r => reloadPlan(r)"/></div>
        <div v-if="gespeichert" class="dp-hint mt-2">
          <v-icon small color="primary">mdi-information-outline</v-icon>
          <span>Gespeicherte Rotation dieser Kolonne: {{ rasterName(gespeichert.azra) }} ab {{ fmtDay(gespeichert.anker_montag) }}.</span>
        </div>
      </section>

      <section class="dp-sec">
        <div class="dp-info">
          <h3>So funktioniert der Vorschlag</h3>
          <div class="dp-flow">
            <template v-for="(s, i) in fluss">
              <v-icon v-if="i" :key="'p' + i" x-small color="grey">mdi-arrow-right</v-icon>
              <span :key="s" class="dp-chip">{{ s }}</span>
            </template>
          </div>
          <p>Ab dem Anker-Montag wird in ganzen Wochen weitergezählt – auch über Monats- und Jahresgrenzen hinweg. Jede Kalenderwoche erhält die Musterwoche
            der Phase, in der sie liegt; nach der letzten Woche der Phase beginnt der Zyklus wieder mit Woche #1.
            Der Anker gehört zur Rotation, nicht zum Mitarbeiter und nicht zum Kalenderjahr.</p>
          <p class="mb-0">Die Vorschau merkt nur Zuordnungen für die Musterzuordnung vor – sie speichert nichts und veröffentlicht nie.</p>
        </div>
        <v-alert type="warning" outlined dense class="mt-3">
          Der Anker-Montag des Zyklus muss vom Kunden bestätigt werden – das Arbeitszeitraster legt ihn nicht fest.
        </v-alert>
        <div class="dp-actions">
          <span class="text-caption grey--text">{{ wochen.length }} Kalenderwochen im Zeitraum · bestehende Zuordnungen werden in der Vorschau angezeigt.</span>
          <v-spacer/>
          <v-btn color="primary" depressed :disabled="!bereit" @click="vorschau"><v-icon left>mdi-eye-outline</v-icon>Vorschau der Rotation</v-btn>
        </div>
        <v-alert v-if="vorgemerkt" type="success" dense text class="mt-3">
          <div class="d-flex align-center flex-wrap" style="gap: 8px">
            <span>{{ vorgemerkt }} Vorschläge vorgemerkt – in der Musterzuordnung prüfen und speichern.</span>
            <v-spacer/>
            <v-btn small depressed color="primary" @click="zurZuordnung">Zur Musterzuordnung<v-icon small right>mdi-arrow-right</v-icon></v-btn>
          </div>
        </v-alert>
      </section>
    </template>

    <bestaetigen-dialog ref="vorschau" titel="Vorschau der Rotation" breit :ok-text="settings.can_edit && vorschlaege.length ? 'Vorschläge vormerken' : ''" @ok="vormerken">
      <p class="text-body-2">
        <b>{{ crewName }}</b> · {{ vorschauRaster }} · Anker {{ fmtDay(anker) }} · {{ vorschlaege.length }} Vorschläge
      </p>
      <v-alert v-if="uebersprungen.length" type="warning" dense text>
        Übersprungen (Musterwoche fehlerhaft): {{ uebersprungen.map(w => 'KW ' + w.nummer).join(', ') }}
      </v-alert>
      <v-simple-table dense class="dp-table">
        <thead><tr><th>Kalenderwoche</th><th>Phase</th><th>Vorschlag</th><th>Soll/Woche</th><th>Aktuell</th></tr></thead>
        <tbody>
        <tr v-for="v in vorschlaege" :key="v.woche.key">
          <td class="text-no-wrap">{{ weekLabel(v.woche) }}</td>
          <td class="text-no-wrap">{{ phaseLabel(v.phase) }}</td>
          <td class="font-weight-medium">{{ vorschauRaster }} / W{{ v.pw.nr }} <span class="text-caption grey--text">({{ v.index + 1 }}/{{ v.phase.wochen.length }})</span></td>
          <td>{{ fmtHHMM(weekHours(v.pw.tage)) }}</td>
          <td>
            <span class="grey--text text--darken-1">{{ aktuell(v.woche.key) }}</span>
            <v-chip v-if="ersetzt(v)" x-small outlined color="orange darken-2" class="ml-1">ersetzt</v-chip>
          </td>
        </tr>
        <tr v-if="!vorschlaege.length"><td colspan="5" class="grey--text">Keine Vorschläge für den Zeitraum.</td></tr>
        </tbody>
      </v-simple-table>
    </bestaetigen-dialog>
  </dp-seite>
</template>

<script>
import {DateTime} from 'luxon'
import {fmtDay, fmtHHMM, inPhase, periodWeeks, phaseLabel, weekHours, weekLabel} from '@/shared/dienstplan_api'
import seite from './seite'
import {dp, effZuordnung, gruppeName, kolonnen, rasterWochen, stageRotation, stageZuordnung, zuordnungLabel} from './store'
import DpSeite from './components/DpSeite'
import DpDateField from './components/DpDateField'
import ZeitraumWahl from './components/ZeitraumWahl'
import BestaetigenDialog from './components/BestaetigenDialog'

const istMontag = d => !!d && DateTime.fromISO(d).weekday === 1

export default {
  name: 'DpRotation',
  mixins: [seite],
  components: {DpSeite, DpDateField, ZeitraumWahl, BestaetigenDialog},
  data: () => ({
    crewId: '',
    azra: '',
    anker: '',
    fluss: ['Woche #1', 'Woche #2', '…', 'wieder #1'],
    vorschlaege: [],
    uebersprungen: [],
    vorschauRaster: '',
    vorgemerkt: 0,
  }),
  computed: {
    kolonnenItems: () => kolonnen(),
    wochen: () => periodWeeks(dp.range.von, dp.range.bis),
    crewName () {
      return this.crewId ? gruppeName(this.crewId) : ''
    },
    // rotation of the crew: staged one first, otherwise the saved one
    gespeichert () {
      if (!this.crewId || !dp.plan) return null
      const s = dp.staged.rotation
      if (s && s.planungsgruppe_id === this.crewId) return s
      return (dp.plan.rotationen || []).find(r => r.planungsgruppe_id === this.crewId) || null
    },
    bereit () {
      return !!this.crewId && !!this.azra && istMontag(this.anker) && this.wochen.length > 0
    },
  },
  watch: {
    crewId () {
      this.vorgemerkt = 0
      if (this.gespeichert) {
        this.azra = this.gespeichert.azra
        this.anker = this.gespeichert.anker_montag
      }
    },
  },
  created () {
    this.ensure().then(() => {
      const items = this.kolonnenItems
      const q = this.$route.query.ziel
      if (q && items.some(g => g.id === q)) this.crewId = q
      else if (items.length) this.crewId = items[0].id
    })
  },
  methods: {
    fmtDay,
    fmtHHMM,
    weekHours,
    weekLabel,
    phaseLabel,
    nurMontag: istMontag,
    rasterName (bisId) {
      return (dp.basis.raster.find(r => r.bis_id === bisId) || {}).name || bisId
    },
    aktuell (iso) {
      return zuordnungLabel(effZuordnung('CREW', this.crewId, iso))
    },
    ersetzt (v) {
      const z = effZuordnung('CREW', this.crewId, v.woche.key)
      return !!z && !(z.azra === this.azra && z.phase_von === v.phase.von && z.woche_nr === v.pw.nr)
    },
    vorschau () {
      this.showLoader()
      rasterWochen(this.azra).then(raster => {
        this.hideLoader()
        const anker = DateTime.fromISO(this.anker)
        const ausserhalb = []
        const liste = []
        const uebersprungen = []
        for (const w of this.wochen) {
          const ph = (raster.phasen || []).find(p => inPhase(w.montag, p.von, p.bis))
          if (!ph || !ph.wochen.length) {
            ausserhalb.push(w)
            continue
          }
          const n = ph.wochen.length
          // whole weeks since the anchor (negative before it), across month and year boundaries
          const offset = Math.round(DateTime.fromISO(w.montag).diff(anker, 'days').days / 7)
          const index = ((offset % n) + n) % n
          const pw = ph.wochen[index]
          if (pw.fehler) {
            uebersprungen.push(w)
            continue
          }
          liste.push({woche: w, phase: ph, pw, index})
        }
        if (ausserhalb.length) {
          this.toast('Phasengrenze: ' + ausserhalb.map(w => 'KW ' + w.nummer).join(', ') + ' liegen in keiner Phase von „' + raster.name +
            '“ vollständig. Bitte den Zeitraum anpassen.', 'red accent-2')
          return
        }
        this.vorschauRaster = raster.name
        this.vorschlaege = liste
        this.uebersprungen = uebersprungen
        this.$refs.vorschau.open()
      }, res => this.fail(res))
    },
    vormerken () {
      if (!this.settings.can_edit) return
      for (const v of this.vorschlaege) {
        stageZuordnung('CREW', this.crewId, v.woche.key, {
          azra: this.azra, raster_name: this.vorschauRaster, phase_von: v.phase.von, phase_bis: v.phase.bis,
          woche_nr: v.pw.nr, hash: v.pw.hash, tage: v.pw.tage,
        })
      }
      stageRotation({planungsgruppe_id: this.crewId, azra: this.azra, anker_montag: this.anker})
      this.$refs.vorschau.close()
      this.vorgemerkt = this.vorschlaege.length
      this.toast(this.vorgemerkt + ' Vorschläge vorgemerkt – in der Musterzuordnung prüfen und speichern.')
    },
    zurZuordnung () {
      this.$router.push({path: '/dienstplanung/zuordnung', query: {typ: 'CREW', ziel: this.crewId, schritt: 5}})
    },
  },
}
</script>

<style scoped>
.dp-sec {
  margin-bottom: 22px;
}
.dp-sec + .dp-sec {
  border-top: 1px solid #e0e0e0;
  padding-top: 18px;
}
.dp-sec h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.dp-sec h2 small {
  font-size: 11px;
  font-weight: 400;
  color: #757575;
  margin-left: auto;
}
.dp-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #455a64;
}
.dp-info {
  border: 1px solid #dfe3e8;
  border-top: 3px solid #3f51b5;
  border-radius: 3px;
  background: #fafbfc;
  padding: 12px 14px;
}
.dp-info h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
}
.dp-info p {
  font-size: 12px;
  color: #546e7a;
  margin: 10px 0 0;
}
.dp-flow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.dp-chip {
  border: 1px solid #c5cae9;
  background: #f1f3fc;
  color: #3f51b5;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  white-space: nowrap;
}
.dp-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid #e0e0e0;
  margin-top: 12px;
  padding-top: 12px;
}
.dp-table th {
  white-space: nowrap;
}
</style>
